import React, { useState, useEffect, useContext, useCallback } from "react";
import Drawer from "./Drawer";
import { UserContext } from "../contexts/UserContext";
import { getWinners, addWinner } from "../services/verbi";
import { VerbiWinner } from "../services/types";
import { useAuthenticatedAction } from "../hooks/useAuthenticatedAction";
import Button from "./Button";
import AutoResizeTextarea from "./AutoResizeTextarea";
import "./VerbiDrawer.css";

interface VerbiDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    postId: number;
    onVerbiUpdate: () => void;
    wordLength?: number;
    verbiSolution?: string;
    userHasWon?: boolean;
}

const VerbiDrawer: React.FC<VerbiDrawerProps> = ({
    isOpen,
    onClose,
    postId,
    onVerbiUpdate,
    wordLength,
    verbiSolution,
    userHasWon,
}) => {
    const { currentUser } = useContext(UserContext);
    const [solution, setSolution] = useState("");
    const [winners, setWinners] = useState<VerbiWinner[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const { execute: submitSolution, loading: isSubmitting } = useAuthenticatedAction();

    // Normalize user input and solution to make comparisons more robust.
    // - Unify various apostrophe characters to ASCII '\''
    // - Strip diacritics (accents) to avoid mismatches due to input methods
    // - Collapse whitespace and trim
    // - Lowercase for case-insensitive comparison
    const normalizeForVerbi = (text: string): string => {
        if (!text) return "";
        const apostrophePattern = /[\u2019\u2018\u02BC\uFF07\u2032]/g; // ’ ‘ ʼ ＇ ′
        const withoutDiacritics = text
            .normalize("NFKD")
            .replace(/[\u0300-\u036f]/g, "");
        return withoutDiacritics
            .replace(apostrophePattern, "'")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase();
    };

    const fetchWinners = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const winnersResponse = await getWinners(postId);
            
            // Winners are objects with display_name, ID, and id
            setWinners(winnersResponse.winners);
        } catch (err) {
            setError("Erreur lors du chargement des gagnants");
            console.error("Failed to fetch winners:", err);
        } finally {
            setLoading(false);
        }
    }, [postId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!solution.trim() || !currentUser || !verbiSolution) return;

        // Clear previous validation error
        setValidationError(null);

        // Validate the solution client-side using robust normalization
        const normalizedSolution = normalizeForVerbi(solution);
        const normalizedVerbiSolution = normalizeForVerbi(verbiSolution);

        if (normalizedSolution !== normalizedVerbiSolution) {
            setValidationError("Mauvaise réponse ! Essayez encore.");
            return;
        }
        
        const result = await submitSolution(addWinner, postId, currentUser.id);
        if (result) {
            // Success - refresh data and close drawer
            setSolution("");
            setValidationError(null);
            onVerbiUpdate(); // Refresh parent component
            await fetchWinners(); // Refresh winners list
            onClose();
        } else {
            setValidationError("Erreur lors de la soumission. Veuillez réessayer.");
        }
    };

    const handleClose = () => {
        setSolution("");
        setValidationError(null);
        onClose();
    };

    useEffect(() => {
        if (isOpen) {
            fetchWinners();
        }
    }, [isOpen, fetchWinners]);

    return (
        <Drawer
            isOpen={isOpen}
            onClose={handleClose}
            title="Répondre au Verbi"
        >
            <div className="VerbiDrawer-content">
                <div className="VerbiDrawer-solvers">
                    <h3>Ont déjà trouvé ({winners.length}) :</h3>
                    {loading ? (
                        <p>Chargement des gagnants...</p>
                    ) : error ? (
                        <p className="VerbiDrawer-error">{error}</p>
                    ) : (
                        <ol className="VerbiDrawer-solversList">
                            {winners.map((winner, index) => {
                                const isCurrentUser = currentUser && String(winner.id) === String(currentUser.id);
                                return (
                                    <li 
                                        key={winner.id || index} 
                                        className={`VerbiDrawer-solver ${isCurrentUser ? 'VerbiDrawer-currentUser' : ''}`}
                                    >
                                        <span className="VerbiDrawer-solverNumber">{index + 1}.</span>
                                        <span className="VerbiDrawer-solverId">
                                            {winner.display_name}
                                            {isCurrentUser && <span className="VerbiDrawer-youLabel"> (vous)</span>}
                                        </span>
                                    </li>
                                );
                            })}
                            {winners.length === 0 && (
                                <li className="VerbiDrawer-noWinners">
                                    Aucun gagnant pour le moment. Soyez le premier !
                                </li>
                            )}
                        </ol>
                    )}
                </div>

                {!userHasWon && (
                    <div className="VerbiDrawer-form">
                        {validationError && (
                            <div className="VerbiDrawer-validationError">
                                {validationError}
                            </div>
                        )}
                        <form onSubmit={handleSubmit} className="VerbiDrawer-inlineForm">
                            <AutoResizeTextarea
                                id="solution"
                                name="solution"
                                value={solution}
                                onChange={(e) => {
                                    setSolution(e.target.value);
                                    // Clear validation error when user starts typing
                                    if (validationError) {
                                        setValidationError(null);
                                    }
                                }}
                                placeholder={wordLength ? `Votre réponse (${wordLength} lettres)` : "Votre réponse"}
                                disabled={isSubmitting}
                                autoComplete="off"
                                minRows={1}
                                maxRows={3}
                            />
                            <Button
                                type="submit"
                                variant="primary"
                                size="small"
                                disabled={!solution.trim() || !verbiSolution}
                                loading={isSubmitting}
                            >
                                Envoyer
                            </Button>
                        </form>
                    </div>
                )}
            </div>
        </Drawer>
    );
};

export default VerbiDrawer;
